import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { showLoading } from 'react-global-loading';
import 'react-chatbot-kit/build/main.css';
import { HomeRoute } from '../routes/Home';
import { PeopleRoute } from '../routes/People';
import { SettingsRoute } from '../routes/Settings';
import { ProfileRoute } from '../routes/Profile';
import { CalendarRoute } from '../routes/Calendar';
import { ProtectedEventRoute } from '../routes/Event';
import { ControlPanelLayout } from '../ui/layouts/ControlPanel';
import { ErrorLayout } from '../ui/layouts/Error';
import { QueryClientProvider } from 'react-query';
import { ClerkProvider } from '@clerk/clerk-react';
import {
  NotificationContext,
  NotificationTabs,
} from '../ui/context/notification';
import { useAsync } from 'react-async';
import {
  DecisionConfirmationRoute,
  EMAILED_DECISION_ROUTE,
  LEGACY_DECISION_ROUTE,
} from './components/DecisionConfirmation';
import { Loader } from '../ui/Loader';
import { LEGACY_REDIRECTS } from './components/LegacyRedirects';
import { RequireRole } from './components/RequireRole';
import { getConfig, getOnboardingData, getOrganisation } from '../utils';
import { useAppInitialization } from '../hooks/useAppInitialization';
import { GlobalAction } from '../reducer/GlobalReducer';
import { OnboardingLayout } from '../ui/layouts/OnboardingLayout';
import ReportRoute from '../routes/Reports';
import { JobRoute } from '../routes/Job';
import CandidateDetail from '../routes/Job/CandidateDetail';
import DocumentManagement from '../routes/DocumentManagement';
import { OrganizationRoute } from '../routes/Organization';
import TimeOffRoute from '../routes/TimeOff';
import { TimesheetsRoute } from '../routes/Timesheets';
import { RotaManagementRoute } from '../routes/RotaManagement';
import { MyShiftsRoute } from '../routes/MyShifts';
import { ChatbotModalProvider } from '../ui/context/chatbot';
import DetailedReport from '../routes/Reports/DetailedReport';
import BradfordFactor from '../routes/Reports/BradfordFactor';
import AbsenceTrends from '../routes/Reports/AbsenceTrends';
import LeaveBalanceReport from '../routes/Reports/LeaveBalanceReport';
import SummaryDashboard from '../routes/Reports/SummaryDashboard';
import SavedReports from '../routes/Reports/SavedReports';

import { EmployeeDetailsRoute } from '../routes/TimeOff/routes/EmployeeDetailsRoute';
import AbsenteeismRiskReport from '../routes/Reports/AbsenteeismRiskReport';
import ReportScheduler from '../routes/Reports/ReportScheduler';
import AppSumo from '../routes/AppSumo';
import NotificationsPage from '../routes/Notifications';

export function AuthWrapper() {
  const { data: response, isPending } = useAsync({
    promiseFn: getOrganisation,
  });

  const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

  if (!PUBLISHABLE_KEY) {
    throw new Error('Missing Publishable Key');
  }

  useEffect(() => {
    if (isPending) {
      showLoading(true);
    } else {
      showLoading(false);
    }
  }, [response, isPending]);

  if (isPending) return <Loader />;
  if (response)
    return (
      <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
        <ChatbotModalProvider>
          <App />
        </ChatbotModalProvider>
      </ClerkProvider>
    );

  return <Loader />;
}

export function App() {
  const { loaderActive, queryClient, state, dispatch, token } =
    useAppInitialization();

  const [onBoardingIsPending, setOnBoardingIsPending] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (token) {
          await getConfig(token);
          await getOnboardingData(token);
          setOnBoardingIsPending(false);
        }
      } catch (error) {
        console.error('Error fetching onboarding data:', error);
        setOnBoardingIsPending(false);
      }
    };

    fetchData();
  }, [token]);

  if (loaderActive || onBoardingIsPending) {
    return <Loader />;
  }

  return (
    <NotificationContext.Provider
      value={{
        isActive: (tab: NotificationTabs) => state.notification.auth.login,
        setActive: (tab: NotificationTabs) =>
          dispatch({ type: GlobalAction.ActivateNotification, value: tab }),
      }}
    >
      <QueryClientProvider client={queryClient}>
        <Routes>
          {/* Entry points linked from the marketing site; once signed in, go home. */}
          <Route path="/sign-in" element={<Navigate to="/" replace />} />
          <Route path="/sign-up" element={<Navigate to="/" replace />} />
          <Route path="/onboarding" element={<OnboardingLayout />} />
          {LEGACY_REDIRECTS.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
          <Route
            path="/my-shifts"
            element={
              <ControlPanelLayout>
                <MyShiftsRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/timeaway/:id"
            element={
              <ControlPanelLayout>
                <ProtectedEventRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/"
            element={
              <ControlPanelLayout>
                <HomeRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/calendar"
            element={
              <ControlPanelLayout>
                <CalendarRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path={EMAILED_DECISION_ROUTE}
            element={
              <ControlPanelLayout>
                <DecisionConfirmationRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path={LEGACY_DECISION_ROUTE}
            element={
              <ControlPanelLayout>
                <DecisionConfirmationRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/time-off"
            element={
              <ControlPanelLayout>
                <TimeOffRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/time-off/:id"
            element={
              <ControlPanelLayout>
                <EmployeeDetailsRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/profile"
            element={
              <ControlPanelLayout>
                <ProfileRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/profile/:id"
            element={
              <ControlPanelLayout>
                <ProfileRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/settings"
            element={
              <ControlPanelLayout>
                <RequireRole level="admin">
                  <SettingsRoute />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route path="/report" element={<Navigate to="/reports" replace />} />
          <Route
            path="/reports"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <ReportRoute />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/reports/saved"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <SavedReports />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/reports/summary"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <SummaryDashboard />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/reports/absence"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <DetailedReport />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/reports/bradford-factor"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <BradfordFactor />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/reports/absence-trends"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <AbsenceTrends />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/reports/leave-balance"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <LeaveBalanceReport />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/reports/absenteeism-risk"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <AbsenteeismRiskReport />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/reports/schedules"
            element={
              <ControlPanelLayout>
                <RequireRole level="admin">
                  <ReportScheduler />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/people"
            element={
              <ControlPanelLayout>
                <RequireRole level="admin">
                  <PeopleRoute />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/ats/candidate/:id"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <CandidateDetail />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/ats"
            element={
              <ControlPanelLayout>
                <RequireRole level="manager">
                  <JobRoute />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/document-management"
            element={
              <ControlPanelLayout>
                <DocumentManagement />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/organization"
            element={
              <ControlPanelLayout>
                <RequireRole level="admin">
                  <OrganizationRoute />
                </RequireRole>
              </ControlPanelLayout>
            }
          />
          <Route
            path="/timesheets"
            element={
              <ControlPanelLayout>
                <TimesheetsRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/rota-management"
            element={
              <ControlPanelLayout>
                <RotaManagementRoute />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/notifications"
            element={
              <ControlPanelLayout>
                <NotificationsPage />
              </ControlPanelLayout>
            }
          />
          <Route
            path="/appsumo"
            element={
              <ControlPanelLayout>
                <AppSumo />
              </ControlPanelLayout>
            }
          />
          <Route path="*" element={<ErrorLayout goHome code={404} />} />
        </Routes>
      </QueryClientProvider>
    </NotificationContext.Provider>
  );
}

export default function MainAppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/*" element={<AuthWrapper />} />
      </Routes>
    </BrowserRouter>
  );
}
